import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from '@services/analytics-service';
import { AnalyticEvents } from '@constants/index';

/*
  RUM action names, FROZEN. sar-spinner-pin-placed and sar-spinner-pin-failed are queried by live
  dashboards, and sar-spinner-pin-failed is the SERV-583 alarm — renaming either silently detaches
  a monitor. Treat the other three the same once they ship.
*/
export const RUM_ROTATED = 'sar-spinner-rotated';
export const RUM_PIN_PLACED = 'sar-spinner-pin-placed';
export const RUM_PIN_FAILED = 'sar-spinner-pin-failed';
export const RUM_TELEMETRY_FAILED = 'sar-spinner-telemetry-failed';
export const RUM_INPUT_DURING_INTRO_SPIN = 'sar-spinner-input-during-intro-spin';

const MAX_ELAPSED_MS = 15 * 60_000;
const MAX_PIN_REPORTS = 25;

export const monotonicNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();

type Payload = Record<string, unknown>;

function report(rumAction: string, rumPayload: Payload, clickstreamEvent: string, clickstreamPayload: Payload): void {
  try {
    datadogRum.addAction(rumAction, rumPayload);
  } catch {
    /* empty */
  }
  setTimeout(() => {
    try {
      Analytics.trackEvent(clickstreamEvent, clickstreamPayload);
    } catch {
      try {
        datadogRum.addAction(RUM_TELEMETRY_FAILED, { sink: 'clickstream', event: rumAction });
      } catch {
        /* empty */
      }
    }
  }, 0);
}

export interface RotationGate {
  userInitiated: boolean;
  ready: boolean;
  step: 'mark' | 'confirm';
  pinching: boolean;
  blocked: boolean;
  pointerCount: number;
  pastDragThreshold: boolean;
}

export function shouldReportRotation(gate: RotationGate): boolean {
  return (
    gate.userInitiated &&
    gate.ready &&
    gate.step === 'mark' &&
    !gate.pinching &&
    !gate.blocked &&
    gate.pointerCount < 2 &&
    gate.pastDragThreshold
  );
}

export interface RotationReportInput extends RotationGate {
  pointerType: string;
  stockNumber: number;
  viewedAt: number | null;
  interactiveAt: number | null;
}

const elapsedSince = (start: number | null, now: number): number | null => {
  if (start === null) return null;
  const ms = now - start;
  return Number.isFinite(ms) && ms >= 0 && ms <= MAX_ELAPSED_MS ? ms : null;
};

export function createRotationReporter({ now = monotonicNow }: { now?: () => number } = {}) {
  let reported = false;
  return (input: RotationReportInput): boolean => {
    if (reported || !shouldReportRotation(input)) return false;
    reported = true;
    const at = now();
    const payload = {
      variant: 'spinner',
      stockNumber: input.stockNumber,
      pointerType: input.pointerType,
      msFromViewed: elapsedSince(input.viewedAt, at),
      msFromInteractive: elapsedSince(input.interactiveAt, at),
    };
    report(RUM_ROTATED, payload, AnalyticEvents.sarLocationSpinnerRotated, payload);
    return true;
  };
}

export const INTRO_SPIN_POINTER_ID = 7777;

export function isIntroSpinPointer(e: { isTrusted: boolean; pointerId: number }): boolean {
  return !e.isTrusted && e.pointerId === INTRO_SPIN_POINTER_ID;
}

export interface IntroSpinInputReport {
  pointerType: string;
  stockNumber: number;
  dropped: boolean;
}

export function createIntroSpinInputReporter() {
  const reported: Record<'ok' | 'dropped', boolean> = { ok: false, dropped: false };
  return (input: IntroSpinInputReport): boolean => {
    const latch = input.dropped ? 'dropped' : 'ok';
    if (reported[latch]) return false;
    reported[latch] = true;
    try {
      datadogRum.addAction(RUM_INPUT_DURING_INTRO_SPIN, {
        variant: 'spinner',
        stockNumber: input.stockNumber,
        pointerType: input.pointerType,
        dropped: input.dropped,
      });
    } catch {
      /* empty */
    }
    return true;
  };
}

export function createPinReporter() {
  const emitted: Record<'placed' | 'failed', number> = { placed: 0, failed: 0 };
  const withinBudget = (outcome: 'placed' | 'failed'): boolean => {
    if (emitted[outcome] >= MAX_PIN_REPORTS) return false;
    emitted[outcome] += 1;
    return true;
  };

  return {
    reportPinPlaced({ stockNumber, degraded }: { stockNumber: number; degraded: boolean }): boolean {
      if (!withinBudget('placed')) return false;
      report(RUM_PIN_PLACED, { stockNumber, degraded }, AnalyticEvents.sarLocationSpinnerPinPlaced, {
        variant: 'spinner',
        stockNumber,
        degraded,
      });
      return true;
    },

    reportPinFailed({ stockNumber }: { stockNumber: number }): boolean {
      if (!withinBudget('failed')) return false;
      report(RUM_PIN_FAILED, { stockNumber }, AnalyticEvents.sarLocationSpinnerPinFailed, {
        variant: 'spinner',
        stockNumber,
      });
      return true;
    },
  };
}
